import * as React from "react"
import { formatPrice } from "@/lib/format-utils"
import type { ChartRange } from "@/lib/collection-chart-range"
import type { CollectionCategoryStats } from "@/lib/collection-category-stats"
import { CollectionDistributionChart } from "./collection-distribution-chart"

/**
 * "{Category} by the Numbers" — FRIDGE-401 Step 4, the head-category data
 * band. Same pattern as the brand pages' BrandNumbersBand (FRIDGE-401
 * Step 1): the card's finding is that retailer category pages hold the head
 * terms, and the copyable counter is measured cross-retailer data a single
 * retailer cannot publish — live model counts with the type split, the
 * cross-retailer price median and range, the live in-stock offer aggregate,
 * ENERGY STAR share, and the capacity span.
 *
 * Real-or-Hidden, tile by tile:
 * - The whole band renders NOTHING without stats (non-head collections pass
 *   null) — no empty shells, no zero-tiles.
 * - Each tile requires its own datum: no offers → no offers tile; no
 *   positive capacities → no capacity tile; zero ENERGY STAR rows → no
 *   ENERGY STAR tile.
 *
 * Every figure is page-faithful (lib/collection-category-stats.ts documents
 * the population precisely). When the page's fetch sat at the row cap the
 * model count is a FLOOR and renders "Over N" — the same saturation
 * discipline the hero and the FAQ answers apply (FRIDGE-480/542).
 * Capacity renders to 2 decimals (the sitewide spec-precision rule).
 * Prices — the median and the price range — render to the NEAREST DOLLAR
 * (owner rule, FRIDGE-1021, 09/15/2026): a band figure is an aggregate, and
 * "$34.99–$17,909.99" reads as two offers, not a range.
 *
 * FRIDGE-1021 also puts the band on EVERY collection page: non-head slugs
 * receive `statsFromMembers` (the page's own member rows) from the render,
 * so `stats` is no longer null outside the two head categories.
 */

interface Tile {
  label: string
  value: string
  sub?: string
}

export function CollectionNumbersBand({
  title,
  stats,
  countIsFloor = false,
  onSelectRange,
  selectedRange = null,
}: {
  title: string
  stats: CollectionCategoryStats | null
  /** FRIDGE-480 saturation truth from the render — the fetch hit the row
   *  cap, so counts are floors, not measurements. */
  countIsFloor?: boolean
  /** FRIDGE-1278 — a chart section filters the listing below. */
  onSelectRange?: (range: ChartRange | null) => void
  selectedRange?: ChartRange | null
}) {
  if (!stats) return null
  if (stats.totalModels <= 0) return null
  if (stats.medianPrice === null && (stats.minPrice === null || stats.maxPrice === null)) return null

  const tiles: Tile[] = []

  tiles.push({
    label: "Models tracked",
    value: `${countIsFloor ? "Over " : ""}${stats.totalModels.toLocaleString("en-US")}`,
    // NO per-type sub here, deliberately: each type collection's population
    // overlaps the head set (tag-overlap membership) and the head count can
    // be a floor, so per-type counts beside this number sum past it —
    // arithmetic that reads broken. The type comparison table below carries
    // the split, exact per linked page.
    sub: "every model compared across retailers",
  })

  if (stats.medianPrice !== null) {
    tiles.push({
      label: "Median tracked price",
      value: formatPrice(Math.round(stats.medianPrice)),
      sub: countIsFloor ? "across the models served here" : "across the live catalog",
    })
  }

  if (stats.minPrice !== null && stats.maxPrice !== null) {
    tiles.push({
      label: "Price range",
      value: `${formatPrice(Math.round(stats.minPrice))}–${formatPrice(Math.round(stats.maxPrice))}`,
      sub: "lowest tracked offer per model",
    })
  }

  if (stats.offerCount > 0) {
    tiles.push({
      label: "Live offers",
      value: stats.offerCount.toLocaleString("en-US"),
      sub:
        stats.retailerCount >= 2
          ? `in stock across ${stats.retailerCount} retailers`
          : "in stock right now",
    })
  }

  if (stats.energyStarCount > 0 && stats.energyStarSharePct !== null) {
    tiles.push({
      label: "ENERGY STAR certified",
      value: `${stats.energyStarSharePct}%`,
      // The share's denominator is the measured set, so the sub names that
      // set precisely — "served here" when the count is a floor, never a
      // percentage claimed over an unmeasured population.
      sub: `${stats.energyStarCount.toLocaleString("en-US")} of ${stats.totalModels.toLocaleString("en-US")} ${countIsFloor ? "models served here" : "tracked models"}`,
    })
  }

  if (stats.capacityMinCuft !== null && stats.capacityMaxCuft !== null) {
    tiles.push({
      label: "Capacity span",
      value: `${stats.capacityMinCuft.toFixed(2)}–${stats.capacityMaxCuft.toFixed(2)}`,
      sub: "cubic feet",
    })
  }

  return (
    <section
      id="collection-numbers"
      aria-label={`${title} by the numbers`}
      className="mb-6 scroll-mt-[110px]"
    >
      <div className="rounded-2xl border border-gray-200 bg-white px-5 py-5 sm:px-7 sm:py-6">
        <h2 className="mb-4 text-[17px] font-black tracking-[-0.01em] text-gray-900 sm:text-[19px]">
          {title} by the Numbers
        </h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3 lg:grid-cols-6">
          {tiles.map((t) => (
            <div key={t.label} className="min-w-0">
              <dt className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
                {t.label}
              </dt>
              <dd className="mt-1">
                {/* break-words, never truncate: a measured figure with an
                    ellipsis in it ("$34.99–$…") is a wrong number. The wide
                    price range wraps to a second line instead. */}
                <span className="block break-words text-[20px] font-black leading-tight tracking-[-0.02em] text-gray-900 sm:text-[22px]">
                  {t.value}
                </span>
                {t.sub && (
                  <span className="mt-0.5 block text-[12px] leading-snug text-gray-500">
                    {t.sub}
                  </span>
                )}
              </dd>
            </div>
          ))}
        </dl>
        {/* FRIDGE-1023 — the interactive layer: how the members spread by
            price and by capacity, hover/keyboard tooltips, table view.
            Renders nothing unless a measure has at least two bins. */}
        <CollectionDistributionChart
          title={title}
          priceBins={stats.priceBins ?? []}
          capacityBins={stats.capacityBins ?? []}
          totalModels={stats.totalModels}
          onSelectRange={onSelectRange}
          selected={selectedRange}
        />
      </div>
    </section>
  )
}
